import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Box, Paper, Typography, Button, CircularProgress, Alert,
    Stepper, Step, StepLabel, Container, TextField, Chip, IconButton, Stack,
    ToggleButton, ToggleButtonGroup, Divider, AppBar, Toolbar,
    List, ListItemButton, ListItemText, Checkbox, FormControlLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import config from './config.json';
import { Tooltip } from '@mui/material';

// --- TYPES ---
interface ReviewItem {
    ReviewId: string;
    SortKey: string;
    content: string;
    type?: string;
    status_flag?: 'PASS' | 'FAIL' | 'PARTIAL'; // Specific to Phase 2
    recommendation?: string; // Specific to Phase 2
    is_user_edited?: boolean;
    related_requirement?: string;
    origin_tender_doc?: string; 
    source_doc?: string;
}

interface ReviewWorkbenchResponse {
    phase: string;
    items: ReviewItem[];
    message?: string;
}

interface CompanyDocument {
    S3LocationPk: string;
    DocumentPath: string;
    Tags: string[];
}

// --- LOCAL API SERVICE ---
const getApiAuthHeaders = (auth: any) => {
    const userApiKey = auth.user?.profile?.['custom:api_key'] as string;
    const id_token = auth.user?.id_token;
    if (!userApiKey || !id_token) {
        throw new Error('Authentication credentials not available. Please log in again.');
    }
    return { 'Content-Type': 'application/json', 'X-Api-Key': userApiKey, 'Authorization': id_token };
};

interface TenderSummary {
    id: string;
    name: string;
    matchingScore: number;
}

const localApiService = {
    fetchTenders: async (auth: any, group?: string): Promise<TenderSummary[]> => {
        const headers: Record<string, string> = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        // /tenders is paginated ({items, cursor}) — follow the cursor;
        // a plain array (pre-pagination lambda) is accepted for deploy skew.
        type TenderRow = { TenderId: string; Name?: string; MatchingScore: number };
        const all: TenderRow[] = [];
        let cursor: string | null = null;
        do {
            const params = new URLSearchParams();
            if (group) params.set('company', group);
            if (cursor) params.set('cursor', cursor);
            const qs = params.toString();
            const res = await fetch(`${baseUrl}/tenders${qs ? `?${qs}` : ''}`, { headers });
            if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch tenders');
            const data: TenderRow[] | { items: TenderRow[]; cursor: string | null } = await res.json();
            if (Array.isArray(data)) {
                all.push(...data);
                cursor = null;
            } else {
                all.push(...(data.items ?? []));
                cursor = data.cursor ?? null;
            }
        } while (cursor);
        return all.map(t => ({
            id: t.TenderId,
            name: t.Name || 'Unnamed Tender',
            matchingScore: t.MatchingScore,
        }));
    },
    fetchCompanyDocuments: async (auth: any): Promise<CompanyDocument[]> => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const companyId = auth.user?.profile?.["cognito:groups"]?.[0];
        if (!companyId) throw new Error("User company group not found.");
        const res = await fetch(`${baseUrl}/company-documents?company_id=${encodeURIComponent(companyId)}`, { headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch company documents');
        return res.json();
    },
    createReview: async (tenderId: string, docPaths: string[], auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/reviews`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ tenderId, docPaths, cognitoGroups: auth.user?.profile?.["cognito:groups"] })
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to start review');
        return res.json();
    },
    getWorkbench: async (reviewId: string, auth: any): Promise<ReviewWorkbenchResponse> => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/reviews/${reviewId}/workbench`, { headers });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    updateItem: async (reviewId: string, itemId: string, data: Partial<ReviewItem>, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const encodedItemId = encodeURIComponent(itemId);
        const res = await fetch(`${baseUrl}/reviews/${reviewId}/items/${encodedItemId}`, { 
            method: 'PUT', 
            headers, 
            body: JSON.stringify(data) 
        });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    approveStage: async (reviewId: string, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/reviews/${reviewId}/stage/approve`, { method: 'POST', headers });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    getArtifactUrl: async (reviewId: string, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/reviews/${reviewId}/artifacts`, { headers });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    }
};

// --- COMPONENT: Compliance Card (Phase 2) ---
const ComplianceCard: React.FC<{ item: ReviewItem; reviewId: string; auth: any }> = ({ item, reviewId, auth }) => {
    const [content, setContent] = useState(item.content);
    const [recommendation, setRecommendation] = useState(item.recommendation || "");
    const [status, setStatus] = useState(item.status_flag || "PARTIAL");
    const [isDirty, setIsDirty] = useState(false);
    const sourceDoc = item.origin_tender_doc || item.source_doc || "Unknown Source";

    const updateMutation = useMutation({
        mutationFn: () => localApiService.updateItem(reviewId, item.SortKey, { 
            content, 
            recommendation, 
            status_flag: status 
        }, auth),
        onSuccess: () => setIsDirty(false)
    });

    let bgColor = 'white';
    let borderColor = '#e0e0e0'; // Default grey

    if (status === 'PASS') {
        bgColor = '#f6fff6'; // Very light green
        borderColor = 'green';
    } else if (status === 'FAIL') {
        bgColor = '#fff5f5'; // Very light red
        borderColor = '#d32f2f';
    } else if (status === 'PARTIAL') {
        bgColor = '#fffbf2'; // Very light orange
        borderColor = '#ed6c02';
    }

    return (
        <Paper 
            variant="outlined" 
            sx={{ 
                p: 2, 
                mb: 2, 
                bgcolor: bgColor, 
                border: `1px solid ${borderColor}`, // Colored border all around
                borderLeft: `6px solid ${borderColor}` // Thicker left accent
            }}
        >
            {item.related_requirement && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '4px solid #1976d2' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="primary" fontWeight="bold" sx={{ mt: 0.5 }}>
                            Requirement to Audit
                        </Typography>
                        <Tooltip title={sourceDoc}>
                            <Chip 
                                label={sourceDoc} 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                    maxWidth: '60%',          // Don't take up whole width
                                    height: 'auto',           // Allow growing vertically
                                    py: 0.5,                  // Add padding for multiline
                                    '& .MuiChip-label': {
                                        display: 'block',
                                        whiteSpace: 'normal', // Force wrap
                                        wordBreak: 'break-word', // Break long strings
                                        fontSize: '0.7rem',
                                        lineHeight: '1.2'
                                    }
                                }} 
                            />
                        </Tooltip>
                    </Box>

                    <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                        "{item.related_requirement}"
                    </Typography>
                </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Chip label={item.SortKey.split('#').pop()} size="small" />
                <ToggleButtonGroup
                    value={status}
                    exclusive
                    onChange={(e, newStatus) => { if(newStatus) { setStatus(newStatus); setIsDirty(true); } }}
                    size="small"
                >
                    <ToggleButton value="PASS" color="success"><CheckCircleIcon fontSize="small" sx={{mr:0.5}}/> Pass</ToggleButton>
                    <ToggleButton value="PARTIAL" color="warning"><WarningAmberIcon fontSize="small" sx={{mr:0.5}}/> Partial</ToggleButton>
                    <ToggleButton value="FAIL" color="error"><ErrorOutlineIcon fontSize="small" sx={{mr:0.5}}/> Fail</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <TextField 
                label="Analysis / Comment"
                fullWidth multiline minRows={2} 
                value={content} onChange={(e) => { setContent(e.target.value); setIsDirty(true); }}
                variant="outlined" sx={{ mb: 2 }}
            />
            
            {(status === 'FAIL' || status === 'PARTIAL') && (
                 <TextField 
                    label="Recommendation to Fix"
                    fullWidth multiline minRows={2} 
                    value={recommendation} onChange={(e) => { setRecommendation(e.target.value); setIsDirty(true); }}
                    variant="filled" color="secondary" sx={{ mb: 1 }}
                />
            )}

            {isDirty && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button startIcon={<SaveIcon />} disabled={updateMutation.isPending} onClick={() => updateMutation.mutate()} size="small" variant="contained">
                        {updateMutation.isPending ? "Saving..." : "Save Assessment"}
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

// --- COMPONENT: Standard Editor (Phase 1 & 3) ---
const StandardItemCard: React.FC<{ item: ReviewItem; reviewId: string; auth: any }> = ({ item, reviewId, auth }) => {
    const [content, setContent] = useState(item.content);
    const [isDirty, setIsDirty] = useState(false);
    const updateMutation = useMutation({
        mutationFn: () => localApiService.updateItem(reviewId, item.SortKey, { content }, auth),
        onSuccess: () => setIsDirty(false)
    });
    const sourceDoc = item.origin_tender_doc || item.source_doc;

    // Determine Severity Colors
    let severityColor: "default" | "error" | "warning" | "success" | "info" = "default";
    let severityLabel = item.type || "ITEM";
    let borderColor = "#e0e0e0";
    let bgColor = "white";

    const severity = (item as any).severity; 

    if (severity === 'HIGH') {
        severityColor = "error";
        severityLabel = "HIGH RISK";
        borderColor = "#d32f2f";
        bgColor = "#fff5f5"; // Light Red
    } else if (severity === 'MEDIUM') {
        severityColor = "warning";
        severityLabel = "MEDIUM RISK";
        borderColor = "#ed6c02";
        bgColor = "#fffbf2"; // Light Orange
    } else if (severity === 'LOW') {
        severityColor = "info";
        severityLabel = "LOW RISK";
        borderColor = "#0288d1";
        bgColor = "#f0f7ff"; // Light Blue
    }

    return (
        <Paper 
            variant="outlined" 
            sx={{ 
                p: 2, 
                mb: 2, 
                bgcolor: bgColor,
                border: `1px solid ${borderColor}`,
                borderLeft: severity ? `6px solid ${borderColor}` : undefined 
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    
                    {/* SEVERITY BADGE */}
                    <Chip 
                        label={severityLabel} 
                        size="small" 
                        color={severityColor} 
                        variant={severity ? "filled" : "outlined"} 
                        sx={{ fontWeight: 'bold' }}
                    />

                    {/* SOURCE CHIP */}
                    {sourceDoc && (
                        <Tooltip title={String(sourceDoc)}>
                            <Chip 
                                label={String(sourceDoc)}
                                size="small" 
                                sx={{ 
                                    height: 'auto',
                                    py: 0.5,
                                    maxWidth: '300px',
                                    bgcolor: 'rgba(0, 0, 0, 0.08)',
                                    '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        fontSize: '0.75rem'
                                    }
                                }} 
                            />
                        </Tooltip>
                    )}
                </Box>
                {isDirty && <Chip label="Unsaved" color="warning" size="small" />}
            </Box>

            <TextField 
                fullWidth 
                multiline 
                minRows={1} 
                value={content} 
                onChange={(e) => { setContent(e.target.value); setIsDirty(true); }}
                sx={{ 
                    bgcolor: 'white', // Keep text area white for readability
                    '& .MuiOutlinedInput-root': { fieldset: { borderColor: 'transparent' } }
                }}
            />
            
            {/* Show Recommendation if it exists (Phase 3 often has it) */}
            {(item.recommendation) && (
                 <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1, border: '1px dashed #ccc' }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary">Recommendation:</Typography>
                    <Typography variant="body2">{item.recommendation}</Typography>
                 </Box>
            )}

            {isDirty && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button startIcon={<SaveIcon />} disabled={updateMutation.isPending} onClick={() => updateMutation.mutate()} size="small" variant="contained">
                        Save
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

// --- MAIN PAGE ---
const ReviewAuditPage: React.FC = () => {
    const { reviewId: routeReviewId, tenderId } = useParams<{ reviewId?: string; tenderId?: string }>();
    const navigate = useNavigate();
    const auth = useAuth();
    const queryClient = useQueryClient();

    const [reviewId, setReviewId] = useState<string | null>(routeReviewId || null);
    const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
    const [activeStep, setActiveStep] = useState(reviewId ? 0 : 0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const steps = ['Select Documents', 'Extract Criteria', 'Compliance Check', 'Blindspot Analysis', 'Final Report'];

    const userPrimaryGroup = (auth.user?.profile?.["cognito:groups"] as string[] | undefined)?.[0];
    const isAuthReady = auth.isAuthenticated && !!auth.user?.profile?.['custom:api_key'];

    // Shares the TenderQPage cache when user navigated from there; otherwise fetches once.
    // Only meaningful in "new review" mode — skip for existing reviews where tenderId isn't in URL.
    const tendersQuery = useQuery({
        queryKey: ['tenders', userPrimaryGroup],
        queryFn: () => localApiService.fetchTenders(auth, userPrimaryGroup),
        enabled: isAuthReady && !!tenderId,
    });
    const tender = (tendersQuery.data as TenderSummary[] | undefined)?.find(t => t.id === tenderId);
    const scorePercent = tender
        ? (tender.matchingScore <= 1 ? Math.round(tender.matchingScore * 100) : Math.round(tender.matchingScore))
        : 0;
    const scoreColor: 'success' | 'warning' | 'error' = scorePercent >= 70 ? 'success' : scorePercent >= 40 ? 'warning' : 'error';
    const scoreLabel = scorePercent >= 70 ? 'Good Fit' : scorePercent >= 40 ? 'Moderate Fit' : 'Low Fit';

    // Fetch company documents (only in "new" mode)
    const companyDocsQuery = useQuery<CompanyDocument[], Error>({
        queryKey: ['companyDocuments', userPrimaryGroup],
        queryFn: () => localApiService.fetchCompanyDocuments(auth),
        enabled: !reviewId && !!userPrimaryGroup,
    });
    const companyDocuments = companyDocsQuery.data ?? [];

    // Create review mutation (new mode)
    const createReviewMutation = useMutation({
        mutationFn: () => localApiService.createReview(tenderId!, Array.from(selectedDocs), auth),
        onSuccess: (data) => {
            setReviewId(data.reviewId);
            setActiveStep(1);
            // Update URL so user can refresh/bookmark
            navigate(`/reviews/${data.reviewId}/audit`, { replace: true });
        }
    });

    // Poll Status
    const workbenchQuery = useQuery({
        queryKey: ['reviewWorkbench', reviewId],
        queryFn: () => localApiService.getWorkbench(reviewId!, auth),
        enabled: !!reviewId,
        refetchInterval: (query) => {
            const phase = query.state.data?.phase;
            if (isTransitioning) return 1000; // Fast poll during transition
            if (['PHASE_1', 'PHASE_2', 'PHASE_3', 'DONE'].includes(phase || '')) return false;
            return 2000;
        },
    });

    // Approve Phase
    const approveMutation = useMutation({
        mutationFn: () => localApiService.approveStage(reviewId!, auth),
        onMutate: () => setIsTransitioning(true),
        onSuccess: () => workbenchQuery.refetch(),
        onError: () => setIsTransitioning(false)
    });

    const phase = workbenchQuery.data?.phase;
    let items = workbenchQuery.data?.items || [];

    // --- SORTING LOGIC ---
    // Priority: FAIL (0) -> PARTIAL (1) -> PASS (2) -> Others (3)
    const statusPriority = { 'FAIL': 0, 'PARTIAL': 1, 'PASS': 2 };

    if (phase === 'PHASE_2') {
        items = [...items].sort((a, b) => {
            const pA = statusPriority[a.status_flag as keyof typeof statusPriority] ?? 3;
            const pB = statusPriority[b.status_flag as keyof typeof statusPriority] ?? 3;
            return pA - pB;
        });
    }

    if (phase === 'PHASE_3') {
        const severityPriority = { 'HIGH': 0, 'MEDIUM': 1, 'LOW': 2 };
        
        items = [...items].sort((a, b) => {
            const sevA = (a as any).severity || 'LOW';
            const sevB = (b as any).severity || 'LOW';
            
            const pA = severityPriority[sevA as keyof typeof severityPriority] ?? 3;
            const pB = severityPriority[sevB as keyof typeof severityPriority] ?? 3;
            return pA - pB;
        });
    }

    // State Logic
    useEffect(() => {
        if (!phase) return;
        if (phase === 'PHASE_1') setActiveStep(1);
        else if (phase === 'PHASE_2') { setActiveStep(2); setIsTransitioning(false); }
        else if (phase === 'PHASE_3') { setActiveStep(3); setIsTransitioning(false); }
        else if (phase === 'DONE') { setActiveStep(4); setIsTransitioning(false); }
        else if (phase === 'STARTING') setActiveStep(0);
    }, [phase]);

    // Download Report
    const handleDownload = async () => {
        try {
            const data = await localApiService.getArtifactUrl(reviewId!, auth);
            if(data.downloadUrl) {
                window.open(data.downloadUrl, '_blank');
            }
        } catch(e) { 
            alert("Download failed"); 
        }
    };

    const renderLoading = (msg: string) => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">{msg}</Typography>
            <Typography variant="body2" color="text.secondary">Analyzing documents...</Typography>
        </Box>
    );

    const renderFailure = (errorMsg: string) => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom>Processing Failed</Typography>
            <Typography variant="body1" color="error" sx={{ mb: 4 }}>
                The automated process encountered an error: **{errorMsg}**
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Please check the Step Functions execution history for details or contact support.
            </Typography>
            <Button 
                variant="contained" 
                size="large" 
                color="primary"
                onClick={() => navigate('/tenderq')}
                sx={{ mt: 3 }}
            >
                Back to Tenders
            </Button>
        </Box>
    );

    return (
        <Box sx={{ 
            bgcolor: '#f8f9fa', 
            height: '100vh', 
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'hidden'
        }}>
            <Box sx={{ 
                p: 2, 
                bgcolor: 'white', 
                borderBottom: '1px solid #ddd', 
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center'
            }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tenderq')}>
                    Back to Tenders
                </Button>
            </Box>

            {/* Scrollable Content Area */}
            <Box sx={{ 
                flexGrow: 1, 
                overflowY: 'auto',
                p: 3
            }}>
                <Container maxWidth="md">
                    <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                            Review & Audit Agent
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="h5" fontWeight="bold">
                                {tender?.name ?? (tenderId && tendersQuery.isLoading ? 'Loading tender...' : tenderId ? 'Unnamed Tender' : 'Existing Review')}
                            </Typography>
                            {tender && (
                                <Chip
                                    label={`${scoreLabel} · ${scorePercent}%`}
                                    color={scoreColor}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 4 }}>
                            {reviewId ? `Review ID: ${reviewId}` : `Tender ID: ${tenderId}`}
                        </Typography>

                        <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Paper>

                    {/* CONTENT PAPER */}
                    <Paper elevation={1} sx={{ p: 4, minHeight: '400px', mb: 8 }}>

                        {/* NOT STARTED - Document Selection */}
                        {!reviewId && (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    Select the company document(s) you want the AI to audit against the tender requirements.
                                </Alert>

                                {companyDocsQuery.isLoading && (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <CircularProgress size={40} sx={{ mb: 2 }} />
                                        <Typography color="text.secondary">Loading company documents...</Typography>
                                    </Box>
                                )}

                                {companyDocsQuery.isError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        Failed to load documents: {companyDocsQuery.error.message}
                                    </Alert>
                                )}

                                {!companyDocsQuery.isLoading && companyDocuments.length === 0 && !companyDocsQuery.isError && (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <FolderIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
                                        <Typography color="text.secondary">No company documents found.</Typography>
                                        <Typography variant="caption" color="text.disabled">
                                            Please upload your draft proposal in the "Documents" section first.
                                        </Typography>
                                    </Box>
                                )}

                                {companyDocuments.length > 0 && (
                                    <List dense sx={{ mb: 2 }}>
                                        {companyDocuments.map((doc) => {
                                            const isSelected = selectedDocs.has(doc.DocumentPath);
                                            return (
                                                <ListItemButton
                                                    key={doc.S3LocationPk}
                                                    selected={isSelected}
                                                    onClick={() => {
                                                        setSelectedDocs(prev => {
                                                            const next = new Set(prev);
                                                            if (next.has(doc.DocumentPath)) next.delete(doc.DocumentPath);
                                                            else next.add(doc.DocumentPath);
                                                            return next;
                                                        });
                                                    }}
                                                    sx={{ borderRadius: 1, mb: 0.5, border: '1px solid', borderColor: isSelected ? 'primary.main' : 'divider' }}
                                                >
                                                    <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple size="small" />
                                                    <ListItemText
                                                        primary={doc.DocumentPath.split('/').pop()}
                                                        secondary={doc.Tags?.length ? doc.Tags.join(', ') : doc.DocumentPath}
                                                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                                                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                                    />
                                                    <DescriptionIcon fontSize="small" color="action" />
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                )}

                                <Box sx={{ textAlign: 'center', mt: 3 }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={createReviewMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                                        onClick={() => createReviewMutation.mutate()}
                                        disabled={selectedDocs.size === 0 || createReviewMutation.isPending}
                                    >
                                        {createReviewMutation.isPending ? "Initializing..." : `Review ${selectedDocs.size} Document(s)`}
                                    </Button>
                                    {createReviewMutation.isError && (
                                        <Alert severity="error" sx={{ mt: 2 }}>
                                            {createReviewMutation.error.message}
                                        </Alert>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* ERROR CHECK */}
                        {reviewId && workbenchQuery.isError && (
                            renderFailure(workbenchQuery.error.message)
                        )}
                        
                        {/* LOADING STATES */}
                        {reviewId && !workbenchQuery.isError && (isTransitioning || approveMutation.isPending || phase === 'STARTING' || phase === 'PROCESSING' || !phase) && (
                            renderLoading(
                                activeStep === 0 ? "Extracting Audit Criteria..." :
                                activeStep === 1 ? "Checking Compliance..." :
                                activeStep === 2 ? "Analyzing Blindspots..." : "Finalizing Report..."
                            )
                        )}

                        {/* PHASE 1: CRITERIA */}
                        {phase === 'PHASE_1' && !isTransitioning && !workbenchQuery.isError && (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    The AI extracted these requirements to audit your documents against. Verify them.
                                </Alert>
                                {items.map(item => <StandardItemCard key={item.SortKey} item={item} reviewId={reviewId!} auth={auth} />)}
                                <Box sx={{ textAlign: 'center', mt: 4 }}>
                                    <Button 
                                        variant="contained" 
                                        size="large" 
                                        color="primary"
                                        onClick={() => approveMutation.mutate()}
                                        disabled={approveMutation.isPending}
                                    >
                                        Start Compliance Check
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* PHASE 2: COMPLIANCE */}
                        {phase === 'PHASE_2' && !isTransitioning && !workbenchQuery.isError && (
                            <Box>
                                <Alert severity="warning" sx={{ mb: 3 }}>
                                    Review the AI's compliance assessment. You can override the Pass/Fail status.
                                </Alert>
                                {items.map(item => <ComplianceCard key={item.SortKey} item={item} reviewId={reviewId!} auth={auth} />)}
                                <Box sx={{ textAlign: 'center', mt: 4 }}>
                                    <Button 
                                        variant="contained" 
                                        size="large" 
                                        color="primary"
                                        onClick={() => approveMutation.mutate()}
                                        disabled={approveMutation.isPending}
                                    >
                                        Analyze Blindspots
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* PHASE 3: BLINDSPOTS */}
                        {phase === 'PHASE_3' && !isTransitioning && !workbenchQuery.isError && (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    The AI found potential strategic blindspots. Review them for the final report.
                                </Alert>
                                {items.map(item => <StandardItemCard key={item.SortKey} item={item} reviewId={reviewId!} auth={auth} />)}
                                <Box sx={{ textAlign: 'center', mt: 4 }}>
                                    <Button 
                                        variant="contained" 
                                        size="large" 
                                        color="success"
                                        onClick={() => approveMutation.mutate()}
                                        disabled={approveMutation.isPending}
                                    >
                                        Generate Final Report
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* DONE */}
                        {phase === 'DONE' && !isTransitioning && !workbenchQuery.isError && (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
                                <Typography variant="h4" gutterBottom>Audit Complete</Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                                    The review is finished. You can now download the full audit report.
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    size="large" 
                                    startIcon={<DownloadIcon />} 
                                    onClick={handleDownload}
                                >
                                    Download Report
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Container>
            </Box>
        </Box>
    );
};

export default ReviewAuditPage;